/**
 * Mobile-Tab-Bar — fixed unten auf jeder Subdomain-Seite.
 * Quick-Access: Home / Sortiment / Korb / Mein Konto.
 *
 * Nur auf Mobile (md:hidden). Auf Desktop nutzt der User den Sticky-Header.
 */
import { Home, LayoutGrid, ShoppingCart, User as UserIcon } from "lucide-react";
import { getCartCount } from "../lib/cart_store";
import { useCart } from "../lib/cart_store";
import { useCustomer } from "../lib/customer_auth";
import { navigate, usePath } from "../lib/router";
import { useEffect, useState } from "react";

type ThemeLike = {
  accent: string;
  ink: string;
  inkMuted: string;
  cardBg: string;
};

export function ShopTabBar({
  agentCode,
  theme,
  isDark,
  onSortimentClick,
}: {
  agentCode: string;
  theme: ThemeLike;
  isDark: boolean;
  onSortimentClick?: () => void;
}) {
  const path = usePath();
  const { count } = useCart(agentCode);
  const { customer } = useCustomer();

  // count update für Cart-Badge — useCart triggert re-render bei Änderung
  // (per dp-cart-changed event), getCartCount nicht nötig hier
  void getCartCount;

  const isHome = path === "/" || path === "";
  const isKonto = path.startsWith("/konto");
  const isCheckout = path.startsWith("/checkout") || path.startsWith("/bestellung");

  return (
    <nav
      data-bottom-nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-30 border-t"
      style={{
        background: isDark ? "rgba(15,23,42,0.95)" : "rgba(255,255,255,0.95)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
      }}
    >
      <div className="grid grid-cols-4 max-w-md mx-auto">
        <TabButton
          icon={<Home className="size-5" />}
          label="Start"
          active={isHome}
          onClick={() => navigate("/")}
          theme={theme}
        />
        <TabButton
          icon={<LayoutGrid className="size-5" />}
          label="Sortiment"
          active={false}
          onClick={() => {
            if (onSortimentClick) {
              onSortimentClick();
            } else {
              navigate("/");
              // Scroll zu Produkte nach navigate (etwas Delay für SPA-Mount)
              window.setTimeout(() => {
                const el = document.getElementById("produkte");
                if (el) {
                  const y = el.getBoundingClientRect().top + window.scrollY - 60;
                  window.scrollTo({ top: y, behavior: "smooth" });
                }
              }, 150);
            }
          }}
          theme={theme}
        />
        <TabButton
          icon={<ShoppingCart className="size-5" />}
          label="Korb"
          active={isCheckout}
          badge={count}
          onClick={() => navigate(customer ? "/checkout" : "/")}
          theme={theme}
        />
        <TabButton
          icon={<UserIcon className="size-5" />}
          label={customer ? "Konto" : "Login"}
          active={isKonto || path.startsWith("/login") || path.startsWith("/registrieren")}
          onClick={() => navigate(customer ? "/konto" : "/login")}
          theme={theme}
        />
      </div>
    </nav>
  );
}

function TabButton({
  icon,
  label,
  active,
  badge,
  onClick,
  theme,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  badge?: number;
  onClick: () => void;
  theme: ThemeLike;
}) {
  return (
    <button
      onClick={onClick}
      className="relative flex flex-col items-center gap-0.5 py-2 px-1 transition-colors"
      style={{
        color: active ? theme.accent : theme.inkMuted,
      }}
    >
      <div className="relative">
        {icon}
        {badge != null && badge > 0 && (
          <span
            className="absolute -top-1.5 -right-2.5 min-w-[16px] h-[16px] grid place-items-center rounded-full text-[9px] font-bold px-1"
            style={{ background: theme.accent, color: "#fff" }}
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
      <span className="text-[10px] font-medium">{label}</span>
    </button>
  );
}

/** Hook für den Spacer am Page-Ende — Bottom-Padding wenn Tab-Bar aktiv ist. */
export function useTabBarSpacer() {
  const [hasTabBar, setHasTabBar] = useState(false);
  useEffect(() => {
    // Tab-Bar nur auf < md
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setHasTabBar(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return hasTabBar;
}
